import React, { useState, useEffect } from 'react';
import { HiPencil, HiTrash, HiPlus, HiSave, HiX, HiCheck, HiOutlinePhotograph, HiOutlineDocumentText } from 'react-icons/hi';
import { flyersService } from '../../services/flyersService';
import './styles/FlyersManagement.css';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import ExpandableText from './ExpandableText';

function FlyersManagement({ selectedEdition }) {
  const [flyersData, setFlyersData] = useState({
    pageHeader: {
      title: 'Nos Flyers',
      subtitle: '',
      emptyMessage: 'Aucun flyer disponible pour le moment.'
    },
    flyers: []
  });
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fileName: '',
    downloadUrl: '',
    pdfPath: '',
    image: '../images/GSIDAPDF.jpg',
    thumbnailPath: '',
    fileSize: '',
    dateCreated: new Date().toISOString().split('T')[0],
    showOnMedia: false
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [imagePreview, setImagePreview] = useState('../images/GSIDAPDF.jpg');
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (selectedEdition) {
      loadFlyersData();
    }
  }, [selectedEdition]);

  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(originalData) !== JSON.stringify(flyersData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [flyersData, originalData]);

  const loadFlyersData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      console.log('Loading flyers data for edition:', selectedEdition);
      const data = await flyersService.getFlyersData(selectedEdition);
      
      if (!data || ((!data.pageHeader?.title || data.pageHeader.title === 'Nos Flyers') && 
                    (!data.pageHeader?.subtitle || data.pageHeader.subtitle === '') &&
                    (!data.flyers || data.flyers.length === 0))) {
        console.log('No data found, setting defaults');
        setIsDatabaseEmpty(true);
        const defaultData = {
          pageHeader: {
            title: 'Nos Flyers',
            subtitle: 'Découvrez et téléchargez nos documents PDF',
            emptyMessage: 'Aucun flyer disponible pour le moment.'
          },
          flyers: []
        };
        setFlyersData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
      } else {
        console.log('Data found:', data);
        setFlyersData(data);
        setOriginalData(JSON.parse(JSON.stringify(data)));
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading flyers data:', error);
      setIsDatabaseEmpty(true);
      const defaultData = {
        pageHeader: {
          title: 'Nos Flyers',
          subtitle: 'Découvrez et téléchargez nos documents PDF',
          emptyMessage: 'Aucun flyer disponible pour le moment.'
        },
        flyers: []
      };
      setFlyersData(defaultData);
      setOriginalData(JSON.parse(JSON.stringify(defaultData)));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (flyer) => {
    setEditingItem(flyer.id);
    setFormData({
      title: flyer.title,
      description: flyer.description,
      fileName: flyer.fileName,
      downloadUrl: flyer.downloadUrl,
      pdfPath: flyer.pdfPath || '',
      image: flyer.image || '../images/GSIDAPDF.jpg',
      thumbnailPath: flyer.thumbnailPath || '',
      fileSize: flyer.fileSize || '',
      dateCreated: flyer.dateCreated,
      showOnMedia: flyer.showOnMedia || false
    });
    setImagePreview(flyer.image || '../images/GSIDAPDF.jpg');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce flyer ?')) {
      try {
        await flyersService.deleteFlyer(id);
        setFlyersData(prev => ({
          ...prev,
          flyers: prev.flyers.filter(flyer => flyer.id !== id)
        }));
        showSuccessMessage();
      } catch (error) {
        console.error('Error deleting flyer:', error);
        alert('Erreur lors de la suppression du flyer.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await flyersService.updateFlyer(editingItem, formData);
        setFlyersData(prev => ({
          ...prev,
          flyers: prev.flyers.map(flyer =>
            flyer.id === editingItem ? { ...flyer, ...formData, id: editingItem } : flyer
          )
        }));
      } else {
        const newFlyer = await flyersService.addFlyer(formData, selectedEdition);
        setFlyersData(prev => ({
          ...prev,
          flyers: [...prev.flyers, {
            id: newFlyer.id,
            title: newFlyer.title,
            description: newFlyer.description,
            fileName: newFlyer.file_name,
            downloadUrl: newFlyer.pdf_url,
            pdfPath: newFlyer.pdf_path,
            image: newFlyer.thumbnail_url,
            thumbnailPath: newFlyer.thumbnail_path,
            dateCreated: newFlyer.date_created,
            fileSize: newFlyer.file_size,
            displayOrder: newFlyer.display_order
          }]
        }));
      }
      closeModal();
      showSuccessMessage();
    } catch (error) {
      console.error('Error saving flyer:', error);
      alert('Erreur lors de la sauvegarde du flyer.');
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      fileName: '',
      downloadUrl: '',
      pdfPath: '',
      image: '../images/GSIDAPDF.jpg',
      thumbnailPath: '',
      fileSize: '',
      dateCreated: new Date().toISOString().split('T')[0],
      showOnMedia: false
    });
    setImagePreview('../images/GSIDAPDF.jpg');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      fileName: '',
      downloadUrl: '',
      pdfPath: '',
      image: '../images/GSIDAPDF.jpg',
      thumbnailPath: '',
      fileSize: '',
      dateCreated: new Date().toISOString().split('T')[0],
      showOnMedia: false
    });
    setImagePreview('../images/GSIDAPDF.jpg');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image valide.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image est trop volumineuse. Taille maximum: 5MB.');
      return;
    }

    try {
      setUploadingImage(true);

      // Delete old image if exists
      if (formData.thumbnailPath) {
        await flyersService.deleteFlyerFile(formData.thumbnailPath, 'flyer-images');
      }

      // Upload new image
      const uploadResult = await flyersService.uploadFlyerThumbnail(file, selectedEdition);
      
      setFormData(prev => ({ 
        ...prev, 
        image: uploadResult.url,
        thumbnailPath: uploadResult.path
      }));
      setImagePreview(uploadResult.url);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Erreur lors du téléchargement de l\'image.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Veuillez sélectionner un fichier PDF valide.');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximum: 50MB.');
      return;
    }

    try {
      setUploadingPdf(true);

      // Delete old PDF if exists
      if (formData.pdfPath) {
        await flyersService.deleteFlyerFile(formData.pdfPath, 'flyer-files');
      }

      // Upload new PDF
      const uploadResult = await flyersService.uploadFlyerPDF(file, selectedEdition);
      
      // Format file size
      const fileSize = (file.size / 1024 / 1024).toFixed(2) + ' MB';
      
      setFormData(prev => ({ 
        ...prev, 
        downloadUrl: uploadResult.url,
        pdfPath: uploadResult.path,
        fileName: file.name,
        fileSize: fileSize
      }));
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Erreur lors du téléchargement du PDF.');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSave = async () => {
    try {
      await flyersService.updateFlyersPageData(flyersData.pageHeader, selectedEdition);
      setOriginalData(JSON.parse(JSON.stringify(flyersData)));
      setHasUnsavedChanges(false);
      setIsDatabaseEmpty(false);
      showSuccessMessage();
    } catch (error) {
      console.error('Error saving page data:', error);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const handleReset = () => {
    if (originalData && window.confirm('Voulez-vous vraiment annuler toutes les modifications non sauvegardées ?')) {
      setFlyersData(JSON.parse(JSON.stringify(originalData)));
      setHasUnsavedChanges(false);
    }
  };

  const handleUpdatePageHeader = (field, value) => {
    setFlyersData(prev => ({
      ...prev,
      pageHeader: {
        ...prev.pageHeader,
        [field]: value
      }
    }));
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données Flyers est vide pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder" pour initialiser les données.</p>
          </div>
        </div>
      )}

      {/* Save/Reset Actions */}
      {hasUnsavedChanges && (
        <div className="save-actions-bar">
          <div className="save-message">
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <div className="save-buttons">
            <button onClick={handleReset} className="btn-secondary">
              Annuler les modifications
            </button>
            <button onClick={handleSave} className="btn-primary save-btn">
              <HiCheck /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* Page Header Section */}
      <div className="section-header">
        <h3>En-tête de la Page</h3>
      </div>
      <div className="form-group">
        <label>Titre principal:</label>
        <input 
          type="text" 
          value={flyersData.pageHeader.title}
          onChange={(e) => handleUpdatePageHeader('title', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Sous-titre:</label>
        <textarea 
          value={flyersData.pageHeader.subtitle}
          onChange={(e) => handleUpdatePageHeader('subtitle', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Message si aucun flyer:</label>
        <input 
          type="text" 
          value={flyersData.pageHeader.emptyMessage}
          onChange={(e) => handleUpdatePageHeader('emptyMessage', e.target.value)}
        />
      </div>

      {/* Flyers Management */}
      <div className="section-header">
        <h3>Gestion des Flyers</h3>
        <button className="add-btn" onClick={openAddModal}>
          <HiPlus /> Ajouter un flyer
        </button>
      </div>
      
      <div className="card">
        <DataTable 
          value={flyersData.flyers} 
          className="p-datatable-sm"
          stripedRows
          showGridlines
          responsiveLayout="scroll"
          scrollable
          tableStyle={{ tableLayout: 'auto' }}
          emptyMessage={flyersData.pageHeader.emptyMessage}
        >
          <Column 
            header="Aperçu" 
            body={(rowData) => (
              <div className="flex justify-content-center">
                <img 
                  src={rowData.image || 'https://via.placeholder.com/50'} 
                  alt={rowData.title} 
                  style={{ width: '40px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} 
                />
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="title" 
            header="Titre" 
            sortable 
            body={(rowData) => <strong>{rowData.title}</strong>}
            style={{ minWidth: '150px' }}
          />
          <Column 
            field="description" 
            header="Description" 
            body={(rowData) => <ExpandableText text={rowData.description || ''} maxChars={40} />}
            style={{ minWidth: '200px' }}
          />
          <Column 
            field="fileName" 
            header="Fichier" 
            body={(rowData) => (
              <div style={{ fontSize: '11px' }}>
                <div>{rowData.fileName}</div>
                <div style={{ color: '#888' }}>{rowData.fileSize}</div>
              </div>
            )}
            style={{ minWidth: '150px' }}
          />
          <Column 
            field="dateCreated" 
            header="Date" 
            sortable 
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Sur Média" 
            body={(rowData) => (
              <i className={`pi ${rowData.showOnMedia ? 'pi-check-circle text-green-500' : 'pi-times-circle text-red-500'}`} style={{ fontSize: '1.2rem' }}></i>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'center' }}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              <div className="flex gap-2">
                <Button icon="pi pi-pencil" onClick={() => handleEdit(rowData)} rounded outlined severity="info" size="small" />
                <Button icon="pi pi-trash" onClick={() => handleDelete(rowData.id)} rounded outlined severity="danger" size="small" />
                <Button icon="pi pi-external-link" onClick={() => window.open(rowData.downloadUrl, '_blank')} rounded outlined severity="secondary" size="small" />
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
        </DataTable>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingItem ? 'Modifier le flyer' : 'Ajouter un flyer'}</h3>
              <button className="close-btn" onClick={closeModal}>
                <HiX />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  // required
                />
              </div>
              
              <div className="form-group checkbox-group">
                <label className="flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.showOnMedia}
                    onChange={(e) => setFormData({...formData, showOnMedia: e.target.checked})}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>Afficher dans l'aperçu Média (Accueil)</span>
                </label>
              </div>
              
              <div className="form-group">
                <label>Fichier PDF</label>
                <div className="file-upload-section">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfUpload}
                    id="pdf-upload"
                    style={{ display: 'none' }}
                    disabled={uploadingPdf}
                  />
                  <button
                    type="button"
                    className={`btn-secondary ${uploadingPdf ? 'uploading' : ''}`}
                    onClick={() => document.getElementById('pdf-upload').click()}
                    disabled={uploadingPdf}
                  >
                    <HiOutlineDocumentText />
                    {uploadingPdf ? 'Téléchargement...' : 'Choisir un PDF'}
                  </button>
                  {formData.downloadUrl && (
                    <div className="current-file">
                      <HiOutlineDocumentText />
                      <span>Fichier: {formData.fileName}</span>
                      <span>Taille: {formData.fileSize}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label>Image de couverture</label>
                <div className="image-upload-container">
                  <div className="image-preview-container">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="image-preview"
                      style={{ width: '150px', height: '200px', objectFit: 'cover' }}
                    />
                  </div>
                  <div className="upload-controls">
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={handleImageUpload}
                      id="flyer-image-upload"
                      className="file-input"
                      disabled={uploadingImage}
                    />
                    <label htmlFor="flyer-image-upload" className={`upload-btn ${uploadingImage ? 'uploading' : ''}`}>
                      <HiOutlinePhotograph /> 
                      {uploadingImage ? 'Téléchargement...' : 'Choisir une image'}
                    </label>
                    <p className="upload-hint">JPG, PNG ou GIF (max. 5MB)</p>
                  </div>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={closeModal}>
                  Annuler
                </button>
                <button type="submit" className="save-btn">
                  <HiSave /> {editingItem ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FlyersManagement;
